import { Link } from "react-router-dom";
import {
  Plug,
  RefreshCcw,
  Send,
  ArrowRight,
  PieChart,
  Wallet,
  Headphones,
  Star,
  Truck,
  Users, // Added Users icon
} from "lucide-react";

const HowItWorks = () => {
  return (
    <div className="bg-white text-black font-sans">
      {/* Hero */}
      <section className="bg-gradient-to-b from-white to-[#F5F5F5] px-6 py-32 text-center">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight text-black">
            How FullPlateNow Works
          </h1>
          <p className="text-lg md:text-xl text-[#666666] mb-6 max-w-2xl mx-auto">
            We connect your restaurant to every major delivery app, streamline
            your operations, and help you earn more—so you can focus on what you
            do best: making great food.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 text-white bg-[#FF5A5F] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E04F55] transition-all duration-300 shadow-sm hover:shadow-md"
          >
            Get Started Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Step-by-Step Section */}
      <section className="max-w-7xl mx-auto px-6 py-32 grid md:grid-cols-3 gap-12 text-center">
        {[
          {
            step: "STEP 1",
            title: "Connect",
            desc: "Onboard your restaurant in minutes. We handle delivery integration setup, connecting your existing accounts or helping you create new ones.",
            icon: <Plug className="w-6 h-6 text-[#FF5A5F]" />,
          },
          {
            step: "STEP 2",
            title: "Sync",
            desc: "Your menu, pricing, hours, and items stay in sync across DoorDash, Grubhub, Uber Eats, and more, with one update in our dashboard.",
            icon: <RefreshCcw className="w-6 h-6 text-[#FF5A5F]" />,
          },
          {
            step: "STEP 3",
            title: "Deliver",
            desc: "Receive orders, track drivers, and get paid weekly. No fees, no stress—just seamless delivery operations.",
            icon: <Send className="w-6 h-6 text-[#FF5A5F]" />,
          },
        ].map((step, idx) => (
          <div
            key={idx}
            className="bg-[#F5F5F5] p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 text-center"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FF5A5F]/10 mb-4">
              {step.icon}
            </div>
            <p className="text-sm font-semibold text-[#FF5A5F] mb-2">
              {step.step}
            </p>
            <h3 className="text-lg font-semibold mb-2 text-black">
              {step.title}
            </h3>
            <p className="text-sm text-[#666666]">{step.desc}</p>
          </div>
        ))}
      </section>

      {/* How We Support Your Growth Section */}
      <section className="px-6 py-32 bg-white">
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          <div>
            <h2 className="text-4xl font-bold mb-4 text-black">
              How We Support Your Growth
            </h2>
            <p className="text-[#666666] mb-6">
              FullPlateNow goes beyond just connecting you to delivery apps—we
              provide tools and support to help your restaurant thrive:
            </p>
            <ul className="space-y-4 text-sm text-[#666666]">
              <li className="flex items-start gap-3">
                <PieChart className="w-6 h-6 text-[#FF5A5F]" />
                <span>
                  <strong>Actionable Insights:</strong> Access detailed
                  analytics to track your best-performing items, busiest hours,
                  and revenue trends across all platforms.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Wallet className="w-6 h-6 text-[#FF5A5F]" />
                <span>
                  <strong>Transparent Payouts:</strong> Get paid weekly via
                  direct deposit with full visibility into your earnings—no
                  hidden fees or commissions.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Headphones className="w-6 h-6 text-[#FF5A5F]" />
                <span>
                  <strong>Dedicated Support:</strong> Our team is here to help
                  with AI-driven support for quick answers and direct assistance
                  for complex issues.
                </span>
              </li>
            </ul>
          </div>
          <img
            src="https://images.unsplash.com/photo-1664575196044-195f135295df?auto=format&fit=crop&w=800&q=80"
            alt="Restaurant dashboard"
            className="rounded-xl shadow-sm"
          />
        </div>
      </section>

      {/* Why It Works for Restaurants Section */}
      <section className="bg-[#F5F5F5] px-6 py-32">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center text-black">
            Why It Works for Restaurants
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                title: "One Dashboard for All Platforms",
                desc: "Say goodbye to juggling multiple apps. FullPlateNow consolidates orders, updates, and analytics into a single, easy-to-use dashboard, saving you time and reducing errors.",
                icon: <Truck className="w-6 h-6 text-[#20C997]" />,
              },
              {
                title: "Weekly Payouts with Full Earnings",
                desc: "Receive 100% of your base menu prices via weekly direct deposits, with full transparency into your earnings and no commission fees.",
                icon: <Wallet className="w-6 h-6 text-[#20C997]" />,
              },
              {
                title: "No Technical Skills Needed",
                desc: "Our platform is designed for ease of use, with a simple interface and guided setup—no technical expertise required to get started.",
                icon: <Users className="w-6 h-6 text-[#20C997]" />,
              },
              {
                title: "Built-In Customer Support",
                desc: "Access AI-driven support for 90% of inquiries, plus direct communication with our team to resolve issues with delivery services quickly.",
                icon: <Headphones className="w-6 h-6 text-[#20C997]" />,
              },
            ].map((benefit, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-2">
                  {benefit.icon}
                  <h3 className="text-lg font-semibold text-black">
                    {benefit.title}
                  </h3>
                </div>
                <p className="text-sm text-[#666666]">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white px-6 py-32">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center text-black">
            What Our Partners Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  "We saved $3,800 in commissions the first month and had zero failed deliveries. FullPlateNow is a no-brainer.",
                author: "Thai House, San Diego",
              },
              {
                quote:
                  "Managing orders across platforms used to be a nightmare. Now, it’s all in one place, and we’re seeing 20% more revenue.",
                author: "Pizza Haven, Chicago",
              },
              {
                quote:
                  "The onboarding was seamless, and the analytics help us make smarter decisions every day. Highly recommend!",
                author: "Burger Barn, Austin",
              },
            ].map((testimonial, idx) => (
              <div
                key={idx}
                className="bg-[#F5F5F5] p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 text-center"
              >
                <Star className="w-6 h-6 text-[#FF5A5F] mx-auto mb-4" />
                <blockquote className="text-lg italic text-[#666666] mb-4">
                  “{testimonial.quote}”
                </blockquote>
                <p className="font-semibold text-black">{testimonial.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-32 text-center bg-gradient-to-b from-[#F5F5F5] to-white">
        <h2 className="text-4xl font-bold mb-4 text-black">
          Get Started with FullPlateNow
        </h2>
        <p className="text-lg text-[#666666] mb-8 max-w-xl mx-auto">
          It takes just minutes to sync your menu and start earning more with
          FullPlateNow.
        </p>
        <Link
          to="/signup"
          className="inline-flex items-center gap-2 text-white bg-[#FF5A5F] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E04F55] transition-all duration-300 shadow-sm hover:shadow-md"
        >
          Become a Partner
          <ArrowRight className="w-5 h-5" />
        </Link>
      </section>
    </div>
  );
};

export default HowItWorks;
